<template>
  <v-card class="dt">
    <div class="dt__head">
      <div class="dt__heading">
        <span class="dt__title">Lieferzeiten</span>
        <span class="dt__hint" v-if="stats">
          {{ stats.sampleCount }} Lieferungen · letzte {{ stats.windowDays }} Tage · Werktage Mo–Fr · Stand {{ updated }}
        </span>
        <span class="dt__hint" v-else>Noch keine Messwerte, die Statistik füllt sich mit den ersten Zustellungen.</span>
      </div>
      <div class="dt__controls" v-if="stats">
        <div class="dt__chips">
          <button v-for="m in modes" :key="m.key" type="button" class="dt__chip" :class="{ 'dt__chip--active': mode === m.key }" @click="mode = m.key">{{ m.label }}</button>
        </div>
        <div class="dt__chips dt__chips--view">
          <button type="button" class="dt__chip dt__chip--icon" :class="{ 'dt__chip--active': view === 'chart' }" title="Übersicht" @click="view = 'chart'"><v-icon x-small>mdi-chart-timeline-variant</v-icon></button>
          <button type="button" class="dt__chip dt__chip--icon" :class="{ 'dt__chip--active': view === 'table' }" title="Tabelle" @click="view = 'table'"><v-icon x-small>mdi-table</v-icon></button>
        </div>
      </div>
    </div>

    <template v-if="stats && total">
      <!-- Kennzahlen über alle Länder -->
      <div class="dt__kpis">
        <div v-for="k in kpis" :key="k.key" class="kpi">
          <div class="kpi__label">{{ k.label }}</div>
          <div class="kpi__value">{{ fmt(k.median) }}<span class="kpi__unit">Werktage</span></div>
          <div class="kpi__sub">80 % der Lieferungen unter {{ fmt(k.p80) }}</div>
          <TrendChip :delta="k.delta" :recent="k.recent" :n="total.trend && total.trend.n" :base-n="total.trend && total.trend.baseN" />
        </div>
      </div>

      <template v-if="view === 'chart'">
        <!-- Carrier und Fulfiller mit Veränderung -->
        <div class="dt__partners">
          <div class="partner-group" v-if="carriers.length">
            <div class="dt__eyebrow">Carrier · Versand bis Zustellung</div>
            <div class="partner-group__list">
              <div v-for="c in carriers" :key="'c' + c.key" class="partner" :title="`${c.name}: ${c.n} Lieferungen, 80 % unter ${fmt(c.p80)} Werktagen`">
                <span class="partner__name">{{ c.name }}</span>
                <span class="partner__value">{{ fmt(c.median) }}</span>
                <TrendChip :delta="c.delta" :recent="c.recent" :n="c.trendN" :base-n="c.trendBaseN" compact />
              </div>
            </div>
          </div>
          <div class="partner-group" v-if="fulfillers.length">
            <div class="dt__eyebrow">Fulfiller · Übergabe bis Versand</div>
            <div class="partner-group__list">
              <div v-for="f in fulfillers" :key="'f' + f.key" class="partner" :title="`${f.name}: ${f.n} Lieferungen, Übergabe bis Zustellung ${fmt(f.toDelivery)} Werktage`">
                <span class="partner__name">{{ f.name }}</span>
                <span class="partner__value">{{ fmt(f.median) }}</span>
                <TrendChip :delta="f.delta" :recent="f.recent" :n="f.trendN" :base-n="f.trendBaseN" compact />
              </div>
            </div>
          </div>
        </div>

        <!-- Länder als Kacheln: drei Werte, Abweichung zum Schnitt aller Länder farbig, Trend zur Vorperiode -->
        <div class="dt__countries" v-if="rows.length">
          <div class="dt__eyebrow">Länder · Median in Werktagen, Abweichung zum Schnitt aller Länder</div>
          <div class="tiles">
            <div v-for="r in rows" :key="r.cc" class="tile" :class="{ 'tile--thin': r.n < 5 }">
              <div class="tile__head">
                <span class="tile__country">{{ flag(r.cc) }} {{ r.cc }}</span>
                <span class="tile__n">{{ r.n }} Lief.</span>
                <TrendChip :delta="r.delta" :recent="r.recent" :n="r.trendN" :base-n="r.trendBaseN" compact />
              </div>
              <div class="tile__vals">
                <div v-for="v in r.values" :key="v.key" class="tval" :title="`${v.label}: ${fmt(v.median)} Werktage, 80 % unter ${fmt(v.p80)}. Alle Länder: ${fmt(v.ref)}`">
                  <span class="tval__label">{{ v.label }}</span>
                  <span class="tval__value">{{ fmt(v.median) }}</span>
                  <span class="tval__diff" :class="'tval__diff--' + v.tone">{{ v.diffText }}</span>
                </div>
              </div>
              <!-- Etappen-Balken: Hover zeigt alle drei Abschnitte in Werktagen (Malte 14.10.) -->
              <v-tooltip v-if="r.segments.length" bottom color="rgba(40,40,40,0.96)" :open-delay="80">
                <template #activator="{ on }">
                  <div class="tile__barHit" v-on="on">
                    <div class="tile__bar">
                      <span v-for="sg in r.segments" :key="sg.key" class="seg" :class="'seg--' + sg.key" :style="{ width: sg.width + '%' }"></span>
                    </div>
                  </div>
                </template>
                <div class="stage-tip">
                  <div v-for="sg in r.segments" :key="sg.key" class="stage-tip__row">
                    <i class="legend__dot" :class="'seg--' + sg.key"></i>
                    <span class="stage-tip__label">{{ sg.label }}</span>
                    <b class="stage-tip__value">{{ fmt(sg.value) }}</b>
                  </div>
                  <div class="stage-tip__row stage-tip__row--sum">
                    <i></i><span class="stage-tip__label">Gesamt, Bestellung bis Zustellung</span><b class="stage-tip__value">{{ fmt(r.median) }}</b>
                  </div>
                  <div class="stage-tip__note">Median in Werktagen</div>
                </div>
              </v-tooltip>
            </div>
          </div>
        </div>

        <div class="dt__legend">
          <span class="legend"><i class="legend__dot seg--toProduction"></i>Bestellung bis Übergabe</span>
          <span class="legend"><i class="legend__dot seg--toShipping"></i>Übergabe bis Versand</span>
          <span class="legend"><i class="legend__dot seg--toDelivery"></i>Versand bis Zustellung</span>
          <span v-if="misrouted" class="legend legend--note legend--warn"><i class="v-icon mdi mdi-alert-outline"></i> {{ misrouted }} Express-Bestellungen vor dem 15.10. liefen über Standard-Fulfillment und zählen hier als Standard.</span>
          <span class="legend legend--note">Balken: Anteil der Etappen an der Gesamtdauer. Farbige Abweichung: Werktage schneller (grün) oder langsamer (rot) als der Schnitt aller Länder, ab 0,5 Werktagen. Trend: Zustellungen der letzten {{ trendDays }} Tage gegenüber den {{ stats.windowDays - trendDays }} Tagen davor. Feiertage nicht herausgerechnet.</span>
        </div>
      </template>

      <!-- Tabellenansicht -->
      <div class="dt__body" v-else>
        <table class="dt-table">
          <thead>
            <tr>
              <th>Land</th><th class="num">Lieferungen</th>
              <th class="num" title="Bestelleingang → Zustellung, Werktage">ab Bestellung</th>
              <th class="num" title="Übergabe an Fulfiller → Zustellung, Werktage">ab Produktion</th>
              <th class="num" title="Versand → Zustellung, Werktage">ab Versand</th>
              <th class="num" title="Veränderung ab Bestellung, letzte 14 Tage gegen davor">Trend</th>
              <th>Carrier</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in tableRows" :key="r.cc" :class="{ 'dt-table__all': r.cc === 'ALLE' }">
              <td><b>{{ r.cc === 'ALLE' ? 'Alle Länder' : flag(r.cc) + ' ' + r.cc }}</b></td>
              <td class="num">{{ r.n }}</td>
              <td class="num"><Cell :d="r.order" /></td>
              <td class="num"><Cell :d="r.production" /></td>
              <td class="num"><Cell :d="r.shipping" /></td>
              <td class="num"><TrendChip :delta="r.delta" :recent="r.recent" :n="r.trendN" :base-n="r.trendBaseN" compact /></td>
              <td class="dt-table__carriers">
                <span v-for="c in r.carriers" :key="c.name" class="dt-table__carrier" :title="`${c.name}: Median ${fmt(c.median)} Werktage ab Versand, ${c.n} Lieferungen`">
                  {{ c.name }} <b>{{ fmt(c.median) }}</b>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="dt__legend"><span class="legend legend--note">Werktage Mo–Fr, Kalendertage im Tooltip. Median, dahinter das 80-%-Quantil.</span></div>
      </div>
    </template>
    <div v-else-if="stats" class="dt__empty">Für diese Auswahl liegen noch keine Lieferungen vor.</div>
  </v-card>
</template>

<script>
// Gemessene Lieferzeiten je Land, Carrier und Fulfiller (deliveryStats/current, täglich aus den
// 17TRACK-Zustellungen verdichtet, Werktage Mo–Fr). Dieselben Werte speisen die Prognose auf der
// Status-Seite. Trend = Zustellungen der letzten 14 Tage gegen die davor im Fenster, damit auffällt,
// wenn ein Carrier oder Fulfiller plötzlich länger braucht (Malte 14.10.).
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../fb";
import { countryFlagEmoji } from "@/utils";
import dayjs from "dayjs";

const CARRIER_NAMES = { gls: "GLS", dhl: "DHL", dhlexpress: "DHL Express", dpd: "DPD", dpdpolandn: "DPD PL", parcel_flex_adv_pl: "Parcel Flex PL", fedex: "FedEx", fxehdn: "FedEx", fxesppsn: "FedEx", unknown: "Unbekannt" };
const FULFILLER_NAMES = { doboxa: "Doboxa", myposter: "myPoster", picanova: "Picanova", unknown: "Unbekannt" };
const TREND_THRESHOLD = 0.3; // Werktage; darunter gilt es als unverändert
const DIFF_THRESHOLD = 0.5; // Werktage Abweichung zum Schnitt aller Länder, ab der die Kachel sie einfärbt

const fmtNum = (v) => (v === null || v === undefined || Number.isNaN(v) ? "–" : Number(v).toLocaleString("de-DE", { maximumFractionDigits: 1 }));
const bizOf = (d) => (d && d.biz && d.biz.median !== null && d.biz.median !== undefined ? d.biz : d) || null;

/** Veränderung mit Pfeil und Zahl, nie Farbe allein (länger = rot, kürzer = grün) */
const TrendChip = {
  name: "DtTrendChip",
  functional: true,
  props: { delta: { type: Number, default: null }, recent: { type: Number, default: null }, n: { type: Number, default: 0 }, baseN: { type: Number, default: 0 }, compact: { type: Boolean, default: false } },
  render(h, { props }) {
    const cls = ["trend", props.compact ? "trend--compact" : ""];
    if (props.delta === null || props.delta === undefined) {
      const title = `Trend ab 5 Lieferungen je Zeitraum (aktuell ${props.n || 0} / ${props.baseN || 0})`;
      return h("span", { class: [...cls, "trend--na"], attrs: { title } }, [h("i", { class: "v-icon mdi mdi-timer-sand trend__icon" }), props.compact ? "" : " noch kein Vergleich"]);
    }
    const up = props.delta >= TREND_THRESHOLD;
    const down = props.delta <= -TREND_THRESHOLD;
    const tone = up ? "trend--worse" : down ? "trend--better" : "trend--flat";
    const icon = up ? "mdi-arrow-up" : down ? "mdi-arrow-down" : "mdi-minus";
    const sign = props.delta > 0 ? "+" : props.delta < 0 ? "−" : "±";
    const text = `${sign}${fmtNum(Math.abs(props.delta))}`;
    const title = `Letzte 14 Tage: ${fmtNum(props.recent)} Werktage (${props.n} Lieferungen) gegenüber ${props.baseN} davor`;
    return h("span", { class: [...cls, tone], attrs: { title } }, [h("i", { class: `v-icon mdi ${icon} trend__icon` }), ` ${text}`, props.compact ? "" : " Werktage zur Vorperiode"]);
  },
};

const Cell = {
  name: "DtCell",
  functional: true,
  props: { d: { type: Object, default: null } },
  render(h, { props }) {
    const d = props.d;
    if (!d || d.median === null || d.median === undefined) return h("span", { class: "dt-muted" }, "–");
    const b = bizOf(d);
    const title = b !== d ? `${fmtNum(d.median)} / ${fmtNum(d.p80)} Kalendertage` : "Kalendertage (Werktage erst nach dem nächsten Statistiklauf)";
    return h("span", { attrs: { title } }, [h("b", fmtNum(b.median)), h("span", { class: "dt-muted" }, ` / ${fmtNum(b.p80)}`)]);
  },
};

export default {
  name: "DeliveryTimesCard",
  components: { Cell, TrendChip },
  data() {
    return {
      stats: null,
      mode: "standard",
      view: "chart",
      modes: [
        { key: "standard", label: "Standard" },
        { key: "express", label: "Express" },
        { key: "all", label: "Alle" },
      ],
      unsub: null,
    };
  },
  computed: {
    updated() {
      return this.stats && this.stats.updatedAt ? dayjs(this.stats.updatedAt).format("DD.MM. HH:mm") : "";
    },
    /** Express bestellt, aber vor dem Routing-Fix über Standard geliefert (zählt als Standard) */
    misrouted() {
      return (this.stats && this.stats.all && this.stats.all.misroutedExpress) || 0;
    },
    trendDays() {
      const t = this.total && this.total.trend;
      return (t && t.recentDays) || 14;
    },
    /** Kennzahlen über alle Länder im gewählten Modus */
    total() {
      const b = this.stats && this.stats.all && this.stats.all[this.mode];
      return b && b.n ? b : null;
    },
    kpis() {
      const t = this.total;
      if (!t) return [];
      const tr = t.trend || {};
      const mk = (key, label) => {
        const d = bizOf(t[key]) || {};
        return { key, label, median: d.median, p80: d.p80, delta: tr[key] === undefined ? null : tr[key], recent: tr.recent ? tr.recent[key] : null };
      };
      return [mk("order", "Bestellung bis Zustellung"), mk("production", "Übergabe bis Zustellung"), mk("shipping", "Versand bis Zustellung")];
    },
    carriers() {
      const all = (this.stats && this.stats.all && this.stats.all.carriers) || {};
      return Object.entries(all)
        .filter(([, v]) => v.n >= 3)
        .map(([key, v]) => {
          const d = bizOf(v.shipping) || {};
          const tr = v.trend || {};
          return { key, name: CARRIER_NAMES[key] || key.toUpperCase(), n: v.n, median: d.median, p80: d.p80, delta: tr.shipping === undefined ? null : tr.shipping, recent: tr.recent ? tr.recent.shipping : null, trendN: tr.n || 0, trendBaseN: tr.baseN || 0 };
        })
        .sort((a, b) => b.n - a.n);
    },
    fulfillers() {
      const all = (this.stats && this.stats.all && this.stats.all.fulfillers) || {};
      return Object.entries(all)
        .filter(([, v]) => v.n >= 3)
        .map(([key, v]) => {
          const st = v.stages || {};
          const tr = v.trend || {};
          const prod = bizOf(v.production) || {};
          return { key, name: FULFILLER_NAMES[key.toLowerCase()] || key, n: v.n, median: st.toShipping, toDelivery: prod.median, delta: tr.toShipping === undefined ? null : tr.toShipping, recent: tr.recent ? tr.recent.toShipping : null, trendN: tr.n || 0, trendBaseN: tr.baseN || 0 };
        })
        .sort((a, b) => b.n - a.n);
    },
    /** Länderkacheln: drei Werte mit Abweichung zum Schnitt aller Länder, Etappen-Balken relativ zur Gesamtdauer */
    rows() {
      if (!this.stats || !this.total) return [];
      const ref = { order: (bizOf(this.total.order) || {}).median, production: (bizOf(this.total.production) || {}).median, shipping: (bizOf(this.total.shipping) || {}).median };
      const list = Object.entries(this.stats.countries || {})
        .map(([cc, g]) => {
          const b = g && g[this.mode];
          if (!b || !b.n) return null;
          const d = bizOf(b.order) || {};
          const st = b.stages || {};
          const tr = b.trend || {};
          const values = [["order", "ab Bestellung"], ["production", "ab Übergabe"], ["shipping", "ab Versand"]].map(([key, label]) => {
            const m = bizOf(b[key]) || {};
            const diff = typeof m.median === "number" && typeof ref[key] === "number" ? Math.round((m.median - ref[key]) * 10) / 10 : null;
            const tone = diff === null ? "na" : diff >= DIFF_THRESHOLD ? "worse" : diff <= -DIFF_THRESHOLD ? "better" : "flat";
            const diffText = diff === null ? "" : `${diff > 0 ? "+" : diff < 0 ? "−" : "±"}${fmtNum(Math.abs(diff))}`;
            return { key, label, median: m.median, p80: m.p80, ref: ref[key], diff, tone, diffText };
          });
          const segs = [
            { key: "toProduction", label: "Bestellung bis Übergabe", value: st.toProduction },
            { key: "toShipping", label: "Übergabe bis Versand", value: st.toShipping },
            { key: "toDelivery", label: "Versand bis Zustellung", value: st.toDelivery },
          ].filter((s) => typeof s.value === "number" && s.value > 0);
          const sum = segs.reduce((a, s) => a + s.value, 0);
          return { cc, n: b.n, median: d.median, p80: d.p80, values, segments: segs, sum, delta: tr.order === undefined ? null : tr.order, recent: tr.recent ? tr.recent.order : null, trendN: tr.n || 0, trendBaseN: tr.baseN || 0 };
        })
        .filter(Boolean)
        .sort((a, b) => b.n - a.n);
      // Balken je Kachel in voller Breite: zeigt den Anteil der Etappen, nicht die absolute Länge
      for (const r of list) for (const s of r.segments) s.width = r.sum ? Math.round((s.value / r.sum) * 1000) / 10 : 0;
      return list;
    },
    tableRows() {
      if (!this.stats) return [];
      const toRow = (cc, g) => {
        const b = g && g[this.mode];
        if (!b || !b.n) return null;
        const tr = b.trend || {};
        const carriers = Object.entries(g.carriers || {})
          .map(([key, v]) => ({ name: CARRIER_NAMES[key] || key.toUpperCase(), n: v.n, median: (bizOf(v.shipping) || {}).median }))
          .filter((c) => c.n >= 3)
          .sort((a, b) => b.n - a.n);
        return { cc, n: b.n, order: b.order, production: b.production, shipping: b.shipping, carriers, delta: tr.order === undefined ? null : tr.order, recent: tr.recent ? tr.recent.order : null, trendN: tr.n || 0, trendBaseN: tr.baseN || 0 };
      };
      const rows = Object.entries(this.stats.countries || {})
        .map(([cc, g]) => toRow(cc, g))
        .filter(Boolean)
        .sort((a, b) => b.n - a.n);
      const all = toRow("ALLE", this.stats.all);
      return all ? [...rows, all] : rows;
    },
  },
  mounted() {
    this.unsub = onSnapshot(doc(db, "deliveryStats", "current"), (snap) => {
      this.stats = snap.exists() ? snap.data() : null;
    }, (e) => console.error("deliveryStats", e));
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    fmt: fmtNum,
    flag: countryFlagEmoji,
  },
};
</script>

<style lang="scss" scoped>
$ink: rgba(0, 0, 0, 0.87);
$muted: rgba(0, 0, 0, 0.5);
$line: rgba(0, 0, 0, 0.07);

.dt {
  padding: 18px 20px 16px;
  &__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  &__heading { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
  &__title { font-weight: 700; font-size: 15px; }
  &__hint { font-size: 12px; color: $muted; }
  &__controls { display: flex; gap: 10px; align-items: center; }
  &__chips { display: flex; gap: 6px; &--view { padding-left: 10px; border-left: 1px solid $line; } }
  &__chip {
    border: 1px solid rgba(0, 0, 0, 0.14); border-radius: 999px; padding: 4px 10px; font-size: 12px; background: #fff; cursor: pointer; line-height: 1.2;
    &--active { background: #282828; border-color: #282828; color: #fff; .v-icon { color: #fff; } }
    &--icon { padding: 4px 7px; }
  }
  &__eyebrow { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: $muted; margin-bottom: 8px; }
  &__kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
  &__partners { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px 28px; margin-top: 18px; }
  &__countries { margin-top: 18px; }
  &__legend { display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: center; margin-top: 12px; font-size: 11px; color: $muted; }
  &__empty { padding: 24px 0 8px; font-size: 13px; color: $muted; }
}

.kpi {
  background: #f7f7f9; border-radius: 12px; padding: 12px 14px;
  &__label { font-size: 12px; color: $muted; }
  &__value { font-size: 28px; font-weight: 700; line-height: 1.15; margin-top: 2px; font-variant-numeric: tabular-nums; color: $ink; }
  &__unit { font-size: 12px; font-weight: 500; color: $muted; margin-left: 6px; }
  &__sub { font-size: 11px; color: $muted; margin: 2px 0 6px; }
}

.partner-group {
  &__list { display: flex; flex-wrap: wrap; gap: 8px; }
}
.partner {
  display: inline-flex; align-items: center; gap: 8px; border: 1px solid $line; border-radius: 10px; padding: 6px 10px; background: #fff; font-size: 12px;
  &__name { font-weight: 600; color: $ink; }
  &__value { font-weight: 700; font-variant-numeric: tabular-nums; }
}

.trend {
  display: inline-flex; align-items: center; gap: 2px; font-size: 11px; font-weight: 600; border-radius: 6px; padding: 1px 6px; white-space: nowrap;
  &__icon { font-size: 13px !important; }
  &--worse { background: rgba(216, 73, 63, 0.12); color: #b23a31; .trend__icon { color: #b23a31 !important; } }
  &--better { background: rgba(46, 125, 50, 0.12); color: #2e7d32; .trend__icon { color: #2e7d32 !important; } }
  &--flat { background: rgba(0, 0, 0, 0.06); color: $muted; .trend__icon { color: $muted !important; } }
  &--na { color: rgba(0, 0, 0, 0.35); font-weight: 500; .trend__icon { color: rgba(0, 0, 0, 0.35) !important; } }
  &--compact { padding: 1px 5px; }
}

.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 10px; }
.tile {
  border: 1px solid $line; border-radius: 12px; padding: 10px 12px 10px; background: #fff; display: flex; flex-direction: column; gap: 8px;
  &--thin { opacity: 0.6; }
  &__head { display: flex; align-items: center; gap: 8px; }
  &__country { font-weight: 700; font-size: 14px; white-space: nowrap; }
  &__n { font-size: 11px; color: $muted; margin-right: auto; }
  &__vals { display: flex; flex-direction: column; gap: 3px; }
  &__barHit { padding: 4px 0 2px; cursor: default; }
  &__bar { display: flex; gap: 2px; height: 6px; border-radius: 3px; overflow: hidden; }
}
.stage-tip {
  font-size: 12px; min-width: 220px;
  &__row { display: grid; grid-template-columns: 10px 1fr auto; gap: 8px; align-items: center; padding: 2px 0; &--sum { border-top: 1px solid rgba(255, 255, 255, 0.2); margin-top: 4px; padding-top: 5px; } }
  &__label { color: rgba(255, 255, 255, 0.85); }
  &__value { font-variant-numeric: tabular-nums; }
  &__note { margin-top: 4px; font-size: 10px; color: rgba(255, 255, 255, 0.6); }
}
.tval {
  display: grid; grid-template-columns: 1fr auto 44px; gap: 8px; align-items: baseline; font-size: 12px;
  &__label { color: $muted; }
  &__value { font-weight: 700; font-size: 15px; font-variant-numeric: tabular-nums; color: $ink; }
  &__diff {
    text-align: right; font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums;
    &--worse { color: #b23a31; } &--better { color: #2e7d32; } &--flat { color: rgba(0, 0, 0, 0.35); font-weight: 500; }
  }
}
.seg {
  display: flex; align-items: center; justify-content: center; height: 100%; min-width: 2px; transition: width 0.3s;
  &:first-child { border-radius: 4px 0 0 4px; }
  &:last-child { border-radius: 0 4px 4px 0; }
  &--toProduction { background: #5b8def; }
  &--toShipping { background: #2a9d8f; }
  &--toDelivery { background: #1f4f9a; }
}
.legend {
  display: inline-flex; align-items: center; gap: 6px;
  &__dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
  &--note { flex-basis: 100%; }
  &--warn { color: #b26a00; .v-icon { font-size: 13px; color: #b26a00; } }
}

.dt__body { margin-top: 16px; }
.dt-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
  th { text-align: left; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: $muted; padding: 6px 8px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
  td { padding: 7px 8px; border-bottom: 1px solid rgba(0, 0, 0, 0.05); vertical-align: middle; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  &__all td { background: #f6f6f8; font-weight: 600; }
  &__carriers { display: flex; gap: 6px; flex-wrap: wrap; }
  &__carrier { background: #f1f1f3; border-radius: 6px; padding: 2px 7px; font-size: 11px; }
}
:deep(.dt-muted) { color: rgba(0, 0, 0, 0.45); font-weight: 400; }
@media (max-width: 760px) {
  .dt__kpis { grid-template-columns: 1fr; }
}
</style>
